import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Field } from 'react-final-form';
import { Chip, Stack } from '@mui/material';
import ProfilePictureFieldAdapter from 'components/shared/field-adapters/ProfilePictureFieldAdapter';
import StyledClientProfileHeader from 'common/theme/profile/client/StyledClientProfileHeader';
import StyledClientProfileHeaderDetails from 'common/theme/profile/client/StyledClientProfileHeaderDetails';
import StyledClientProfileHeaderDivider from 'common/theme/profile/client/StyledClientProfileHeaderDivider';
import StyledProfileHeaderText from 'common/theme/profile/StyledProfileHeaderText';
import StyledProfilePictureContainer from 'common/theme/profile/StyledProfilePictureContainer';
import StyledProfileUsername from 'common/theme/profile/StyledProfileUsername';
import { formatCurrency } from 'common/utils/format';

function ClientProfileHeader() {
  const { t } = useTranslation(['labels']);

  const firstName = useSelector((state) => state.auth.user.first_name);
  const fullName = useSelector((state) => state.auth.user.full_name);
  const lastName = useSelector((state) => state.auth.user.last_name);
  const isActivePlus = useSelector((state) => state.auth.user.is_active_plus);
  const plan = useSelector((state) => state.auth.user?.plan_id ?? null);
  const planName = plan?.name ?? null;
  const planSessions = plan?.sessions ?? null;
  const userCountry = useSelector((state) => state.auth.user?.country);
  const activePlans = useSelector((state) => state.auth.user?.active_plans);
  const activePlusDays = useSelector((state) => state.auth.user?.active_plus_days_left ?? 0);

  // Get price based on user's country (Colombia = COP, others = USD)
  // Country is stored as JSON string, so we need to parse it
  const parsedCountry = userCountry ? JSON.parse(userCountry)?.label : null;
  const isColombianUser = parsedCountry === 'Colombia';
  let planPrice = null;
  if (plan) {
    planPrice = isColombianUser ? plan.price_cop : plan.price_usd;
  }
  const currencyLabel = isColombianUser ? 'COP' : 'USD';

  // Active Plus es ADITIVO: su plan (con precio/sesiones) llega en active_plans por plataforma.
  // En cortesías (can_active_plus sin pago) no hay plan asociado → se muestra solo el chip.
  const activePlusPlan = (activePlans || []).find((item) => item.platform === 'active_plus') ?? null;
  let activePlusPrice = null;
  if (activePlusPlan) {
    activePlusPrice = isColombianUser ? activePlusPlan.price_cop : activePlusPlan.price_usd;
  }

  return (
    <StyledClientProfileHeader spacing={{ xs: 1, sm: 2, lg: 4 }}>
      <StyledProfilePictureContainer>
        <Field
          id="photo"
          name="photo"
          component={ProfilePictureFieldAdapter}
          firstName={firstName}
          lastName={lastName}
        />
      </StyledProfilePictureContainer>
      <StyledClientProfileHeaderDetails spacing={{ xs: 1 }}>
        <StyledProfileUsername>{fullName}</StyledProfileUsername>
        {/* Plan principal (si existe). Active Plus es ADITIVO y se muestra en su propia línea debajo;
            si Active Plus es el único plan, su línea queda centrada como plan principal. */}
        {plan ? (
          <Stack
            spacing={{ xs: 0, sm: 2 }}
            direction={{ xs: 'column', sm: 'row' }}
            divider={<StyledClientProfileHeaderDivider orientation="vertical" flexItem />}
            alignItems="center">
            <StyledProfileHeaderText>
              <span>{t('plan', { ns: 'labels' })}</span>
              <span>:</span> <span>{planName}</span>
            </StyledProfileHeaderText>
            <StyledProfileHeaderText>
              <span>{t('planPrice', { ns: 'labels' })}</span>
              <span>:</span> <span>{formatCurrency(planPrice)} {currencyLabel}</span>
            </StyledProfileHeaderText>
            <StyledProfileHeaderText>
              <span>{t('planSessions', { ns: 'labels' })}</span>
              <span>:</span> <span>{planSessions}</span>
            </StyledProfileHeaderText>
          </Stack>
        ) : null}
        {isActivePlus ? (
          <Stack
            spacing={{ xs: 1, sm: 2 }}
            direction={{ xs: 'column', sm: 'row' }}
            divider={<StyledClientProfileHeaderDivider orientation="vertical" flexItem />}
            alignItems="center">
            <Chip
              label="Active Plus"
              size="small"
              sx={{ bgcolor: 'gold.soft', color: 'gold.main', fontWeight: 700 }}
            />
            {activePlusPrice != null ? (
              <StyledProfileHeaderText>
                <span>{t('planPrice', { ns: 'labels' })}</span>
                <span>:</span> <span>{formatCurrency(activePlusPrice)} {currencyLabel}</span>
              </StyledProfileHeaderText>
            ) : null}
            {activePlusDays > 0 ? (
              <StyledProfileHeaderText>
                <span>{t('planDays', { ns: 'labels' })}</span>
                <span>:</span> <span>{activePlusDays}</span>
              </StyledProfileHeaderText>
            ) : null}
          </Stack>
        ) : null}
      </StyledClientProfileHeaderDetails>
    </StyledClientProfileHeader>
  );
}

export default ClientProfileHeader;
